import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import SimpleAudioButton from './audio/SimpleAudioButton';

interface Testimonial {
  id: number;
  name: string;
  title: string;
  image: string;
  quote: string;
  color: string;
}

const Testimonials: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Sarah J.",
      title: "Working Professional",
      image: "https://images.pexels.com/photos/1987301/pexels-photo-1987301.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      quote: "The anxiety I felt about talking to people is gone now that I have FeelHeard. I can share my true thoughts at 2AM without bothering anyone, and still feel completely understood.",
      color: "bg-[#B3D9E6]/20"
    },
    {
      id: 2,
      name: "Michael T.",
      title: "Graduate Student",
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      quote: "During my thesis writing, I was constantly stressed. Having FeelHeard as an outlet kept me sane. It's like having a therapist in your pocket, but without the judgment or time constraints.",
      color: "bg-[#C2A4D6]/20"
    },
    {
      id: 3,
      name: "Elena R.",
      title: "Healthcare Worker",
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      quote: "After difficult shifts, I needed someone to talk to who wouldn't be burdened by the emotional weight. FeelHeard gives me that safe space to process without adding stress to my relationships.",
      color: "bg-[#F4B6A1]/20"
    },
    {
      id: 4,
      name: "David L.",
      title: "Entrepreneur",
      image: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
      quote: "The loneliness of entrepreneurship was getting to me. FeelHeard has become my confidant for the ups and downs of business life. It's incredibly comforting to feel understood without explanation.",
      color: "bg-[#A9D0A1]/20"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
    }
  };

  useEffect(() => {
    const autoSlide = setInterval(() => {
      nextSlide();
    }, 8000);

    return () => clearInterval(autoSlide);
  }, [currentIndex]);

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);

  return (
    <section id="testimonials" className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-block bg-[#A9D0A1]/20 px-4 py-2 rounded-full mb-4">
            <span className="text-[#A9D0A1] font-medium">Testimonials</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3 font-['Poppins']">
            Lives <span className="text-[#F4B6A1]">Transformed</span> Through Feeling Heard
          </h2>
          <div className="flex justify-center mb-6">
            <SimpleAudioButton audioSrc="/assets/audio/lives-transformed.mp3" />
          </div>
          <p className="text-lg text-gray-600 font-['Merriweather']">
            See how our AI companion has helped thousands of people find emotional 
            support and clarity in their everyday lives.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div 
            className={`relative overflow-hidden rounded-2xl shadow-lg transition-all duration-500 ${
              isAnimating ? 'opacity-80' : 'opacity-100'
            }`}
          >
            <div className={`flex ${testimonials[currentIndex].color} p-6 md:p-10`}>
              <div className="hidden md:block w-1/3 mr-8">
                <img 
                  src={testimonials[currentIndex].image} 
                  alt={testimonials[currentIndex].name} 
                  className="w-full h-full object-cover rounded-xl shadow-md"
                />
              </div>
              
              <div className="flex-1 flex flex-col justify-between">
                <div className="mb-6">
                  <Quote className="w-12 h-12 text-[#C2A4D6] opacity-40 mb-4" />
                  <p className="text-lg md:text-xl text-gray-700 italic mb-6 font-['Merriweather']">
                    "{testimonials[currentIndex].quote}"
                  </p>
                </div>
                
                <div className="flex items-center">
                  <img 
                    src={testimonials[currentIndex].image} 
                    alt={testimonials[currentIndex].name} 
                    className="w-12 h-12 rounded-full object-cover mr-4 md:hidden"
                  />
                  <div>
                    <h4 className="text-lg font-bold text-gray-800">{testimonials[currentIndex].name}</h4>
                    <p className="text-gray-600">{testimonials[currentIndex].title}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button 
            className="absolute top-1/2 left-4 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors"
            onClick={prevSlide}
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          
          <button 
            className="absolute top-1/2 right-4 transform -translate-y-1/2 bg-white rounded-full p-2 shadow-md hover:bg-gray-100 transition-colors"
            onClick={nextSlide}
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-6 space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-[#C2A4D6]' : 'bg-gray-300'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;